import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    prefersReducedMotion,
} from "./motion-preference";

export function useAutoplaySequence({
    itemIds,
    activeItemId,
    setActiveItemId,
    intervalMs,
    threshold = 0.2,
    rootMargin = "0px 0px -8% 0px",
}) {
    const targetRef = useRef(null);
    const [isVisible, setVisible] = useState(false);
    const [isStopped, setStopped] = useState(false);

    useEffect(() => {
        if (
            typeof window === "undefined" ||
            !targetRef.current ||
            !("IntersectionObserver" in window) ||
            prefersReducedMotion()
        ) {
            return undefined;
        }

        const observer = new window.IntersectionObserver(
            ([entry]) => {
                if (!entry || !entry.isIntersecting) {
                    return;
                }

                setVisible(true);
                observer.disconnect();
            },
            {
                threshold,
                rootMargin,
            }
        );

        observer.observe(targetRef.current);

        return () => {
            observer.disconnect();
        };
    }, [rootMargin, threshold]);

    const isAutoplaying =
        isVisible &&
        !isStopped &&
        itemIds.length > 1;

    useEffect(() => {
        if (!isAutoplaying) {
            return undefined;
        }

        const currentIndex =
            itemIds.indexOf(activeItemId);

        const nextIndex =
            currentIndex >= 0
                ? (currentIndex + 1) % itemIds.length
                : 0;

        const timerId = window.setTimeout(() => {
            setActiveItemId(itemIds[nextIndex]);
        }, intervalMs);

        return () => {
            window.clearTimeout(timerId);
        };
    }, [
        activeItemId,
        intervalMs,
        isAutoplaying,
        itemIds,
        setActiveItemId,
    ]);

    const stopAutoplay = useCallback(() => {
        setStopped(true);
    }, []);

    const selectItem = useCallback(
        (itemId) => {
            setStopped(true);
            setActiveItemId(itemId);
        },
        [setActiveItemId]
    );

    return {
        targetRef,
        isAutoplaying,
        stopAutoplay,
        selectItem,
    };
}
